import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { useGetManEulaStatusQuery } from '../../../../services/accountsApi';
import type { Account } from '../../../../services/schemas/accountsBackendCodec';
import { resetSelectedAccountId } from '../accountsSlice';
import { AccountLifeCycleChange } from '../dialogs/AccountLifeCycleChange';
import { EditAccount } from '../dialogs/editAccount/EditAccount';
import { AccountDetails } from './AccountDetails';
import { getShowAccountSidebar, getSidebarAccount, setShowAccountSidebar } from './accountSidebarSlice';
import { AddAdminToAccount } from './adminFunctionality/AddAdminToAccount';
import { DeleteAccount } from './adminFunctionality/DeleteAccount';
import { DeleteUserInAccount } from './adminFunctionality/DeleteUserInAccount';
import { useFetchAccountSettingQuery } from '../../../../services/usersApi';
import { useEffect, useState } from 'react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

export const AccountsSidebar = () => {
    const dispatch = useAppDispatch();

    const show = useAppSelector(getShowAccountSidebar);
    const accountInSidebar = useAppSelector(getSidebarAccount);

    const { data: manEulaStatus, isLoading } = useGetManEulaStatusQuery(
        { accountId: accountInSidebar?.id ?? '' },
        { skip: accountInSidebar === undefined }
    );

    const {
        data: accountSetting,
        error: accountSettingError,
        isError: accountSettingIsError,
    } = useFetchAccountSettingQuery(
        { accountId: accountInSidebar?.id ?? '' },
        { skip: accountInSidebar === undefined }
    );

    const [minAccountAcr, setMinAccountAcr] = useState<string | undefined>(undefined);

    const isFetchBaseQueryError = (err: unknown): err is FetchBaseQueryError =>
        typeof err === 'object' && err !== null && 'status' in (err as Record<string, unknown>);

    useEffect(() => {
        if (!accountInSidebar) {
            setMinAccountAcr(undefined);
            return;
        }

        if (!accountSettingIsError) {
            setMinAccountAcr(accountSetting?.min_account_acr ?? undefined);
            return;
        } else if (isFetchBaseQueryError(accountSettingError)) {
            if (accountSettingError.status === 404) {
                setMinAccountAcr('urn:rio:loa:1');
                return;
            }
        }

        setMinAccountAcr(undefined);
    }, [accountInSidebar, accountSetting, accountSettingError, accountSettingIsError]);

    return (
        <Sidebar
            title={'Account Info'}
            titleClassName={'padding-left-10'}
            closed={!show}
            onClose={() => {
                dispatch(setShowAccountSidebar(false));
                dispatch(resetSelectedAccountId());
            }}
            position={'right'}
            fly={true}
            width={500}
        >
            <div className={'height-100pct overflow-y-scroll'}>
                {accountInSidebar && (
                    <>
                        <AccountDetails
                            account={accountInSidebar}
                            eulaStatus={manEulaStatus}
                            minAccountAcr={minAccountAcr}
                        />
                        <ActionArea account={accountInSidebar} />
                    </>
                )}
            </div>
        </Sidebar>
    );
};

const ActionArea = ({ account }: { account: Account }) => (
    <div className={'display-flex flex-nowrap justify-content-center margin-top-50'}>
        <div className={'display-grid gap-20'}>
            <div className={'display-grid gap-25 grid-cols-1 margin-bottom-25'}>
                <EditAccount account={account} />
            </div>
            <div className={'display-grid gap-25 grid-cols-2'}>
                <AccountLifeCycleChange account={account} />
                <DeleteAccount account={account} />
            </div>
            <div className={'display-grid gap-25 grid-cols-2'}>
                <AddAdminToAccount account={account} />
                <DeleteUserInAccount account={account} />
            </div>
        </div>
    </div>
);
